import * as React from 'react';
import { connect } from 'react-redux';
import { isArray, isEmpty } from 'lodash';

import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import Spinner from 'components/spinner/Spinner';

import SOVAudienceCards from './cards/SOVAudienceCards';

import { fetchAudienceData } from 'modules/insights/sov/reducers/audience/collabary/sov-audience';

import { __t, applyTranslations } from 'utils/language';
import { AUDIENCE_FILTER_OPTIONS } from 'modules/insights/sov/utils/sov';

import './sov-audience.scss';

import type {
  Data,
  MixedSelectDropdownItem,
  SOVAudienceByPropName,
  SOVAudience as SOVAudienceData
} from 'types';
import type { SOVReport } from 'modules/insights/sov/types';

applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));
applyTranslations(require('./sov-audience.i18n.json'));

type OwnProps = {
  report: SOVReport
};

type StateProps = {
  audience: Array<SOVAudienceData>,
  isLoading: boolean
};

type DispatchProps = {
  dispatchFetchAudience: (a: SOVReport) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  filter: string
};

const NUM_STATS_TO_DISPLAY = 6;
const SHARED_MSG_KEY = 'sov_shared';
const MSG_KEY = 'sov_audience';

class SOVAudience extends React.PureComponent<Props, State> {
  state = {
    filter: 'country'
  };

  componentDidMount() {
    const { report, dispatchFetchAudience } = this.props;

    dispatchFetchAudience(report);
  }

  getPropName(filter: string): string {
    return `${filter}AudienceData`;
  }

  getResultsByPropName(propName: string): Array<SOVAudienceByPropName> {
    const { audience } = this.props;
    const results: Array<SOVAudienceByPropName> = [];

    audience.forEach(item => {
      const data = item[propName];

      results.push({
        query: item.searchTerm,
        data: isArray(data) ? data.slice(0, NUM_STATS_TO_DISPLAY) : []
      });
    });

    return results;
  }

  getDropdownItems = (): Array<MixedSelectDropdownItem> => (
    Object.keys(AUDIENCE_FILTER_OPTIONS).map(key => ({
      identifier: key,
      isDisabled: false,
      isSelected: this.state.filter === key,
      label: __t(`${MSG_KEY}.filters.label_${AUDIENCE_FILTER_OPTIONS[key]}`),
      value: key
    }))
  );

  hasResults(): boolean {
    const { audience } = this.props;
    const { filter } = this.state;
    const propName = this.getPropName(filter);
    const results = audience.filter(item => !isEmpty(item[propName]));

    return !isEmpty(results);
  }

  handleFilterChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    this.setState({
      filter: selectedItem?.identifier || updatedItems[0].identifier
    });
  };

  renderNoResults(): React.ReactNode {
    return (
      <div className="sov-audience__no-results">
        {__t(`${MSG_KEY}.label_no_results`)}
      </div>
    );
  }

  renderSpinner(): React.ReactNode {
    return (
      <div className="sov-audience__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderFilters(): React.ReactNode {
    const { filter } = this.state;
    const dropdownItems = this.getDropdownItems();

    return (
      <div className="sov-audience__filters">
        <div className="sov-audience__filters-label">
          {__t(`${SHARED_MSG_KEY}.labels.select_kpi`)}:
        </div>
        <MixedSelectDropdown
          key={`sov-audience__filters-dropdown--${filter}`}
          name="sov-audience__filters-dropdown"
          items={dropdownItems}
          withSelectedInList
          className="sov-audience__filters-dropdown"
          onItemsToggle={this.handleFilterChange}
        />
      </div>
    );
  }

  renderResults(): React.ReactNode | undefined | null {
    const { audience } = this.props;
    const { filter } = this.state;

    if (isEmpty(audience)) {
      return null;
    }

    if (!this.hasResults()) {
      return this.renderNoResults();
    }

    const propName = this.getPropName(filter);
    const results = this.getResultsByPropName(propName);

    return (
      <div className="sov-audience__results">
        <SOVAudienceCards
          results={results}
          filter={filter}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const { isLoading } = this.props;

    if (isLoading) {
      return this.renderSpinner();
    }

    return (
      <div className="sov-audience">
        {this.renderFilters()}
        <div className="sov-audience__results-container">
          {this.renderResults()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ sovAudience }): StateProps => ({
  audience: sovAudience.audience,
  isLoading: sovAudience.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchAudience: (report: SOVReport) => dispatch(fetchAudienceData(report))
});

export default connect(mapStateToProps, mapDispatchToProps)(SOVAudience);
